import axios from 'axios';

import { attachForcedLogoutInterceptor } from '@tt/hooks/forcedLogout';

import config from './config';
import { getCookie, removeCookie, setCookie } from './cookie';

const instance = axios.create({
    baseURL:
        config.get('isLocalhost') === 'true' ? 'http://localhost:5000' : config.get('BASE_API_URL'),
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
        'Accept-Language': localStorage.getItem('language') || 'en'
    }
});

const tradingTerminalBaseUrl =
    config.get('TRADING_TERMINAL_BASE_URL') || 'https://tradingterminal.com';

const parseUserCookie = () => {
    try {
        const raw = getCookie('user');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
    } catch {
        return null;
    }
};

const parseManagerStorage = () => {
    try {
        const raw = localStorage.getItem('manager-storage');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.state || null;
    } catch {
        return null;
    }
};

const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

const setInstanceTokenDefaults = (token) => {
    instance.defaults.headers = instance.defaults.headers || {};
    instance.defaults.headers.common = instance.defaults.headers.common || {};
    instance.defaults.headers.common.token = token;
};

instance.interceptors.request.use(
    (originalConfig) => {
        const reqConfig = { ...originalConfig };
        try {
            const cookieUser = parseUserCookie();
            const cookieToken = cookieUser?.token;
            const managerState = parseManagerStorage();
            const viewAsToken = managerState?.viewAs?.newUser?.token;
            const mainViewAsToken = managerState?.viewAs?.mainUser?.token;

            const currentToken = reqConfig?.headers?.token;
            const hasExplicitToken =
                typeof currentToken === 'string'
                    ? currentToken.trim().length > 0
                    : Boolean(currentToken);
            const shouldSkipViewAs = reqConfig?.skipViewAs === true;
            const normalizedViewAsToken = isNonEmptyString(viewAsToken) ? viewAsToken.trim() : '';
            const normalizedMainViewAsToken = isNonEmptyString(mainViewAsToken)
                ? mainViewAsToken.trim()
                : '';
            const isAdminViewAs = Boolean(
                normalizedViewAsToken &&
                normalizedMainViewAsToken &&
                normalizedViewAsToken !== normalizedMainViewAsToken
            );

            if (!shouldSkipViewAs && isAdminViewAs) {
                reqConfig.headers = {
                    ...(reqConfig.headers || {}),
                    'user-token': normalizedViewAsToken
                };
            }

            // Normalize legacy viewAs requests:
            // if token was set to viewAs token, swap it back to main cookie token.
            if (
                !shouldSkipViewAs &&
                isNonEmptyString(cookieToken) &&
                isAdminViewAs &&
                currentToken === normalizedViewAsToken
            ) {
                reqConfig.headers = { ...(reqConfig.headers || {}), token: cookieToken };
            } else if (cookieToken && !hasExplicitToken) {
                // Only inject cookie token when request doesn't already provide one.
                reqConfig.headers = { ...(reqConfig.headers || {}), token: cookieToken };
            }
        } catch {
            // ignore cookie parsing errors
        }

        return reqConfig;
    },
    (error) => Promise.reject(error)
);

attachForcedLogoutInterceptor(instance, {
    onCleanup: () => removeCookie('user'),
    onTokenUpgrade: async (newToken) => {
        const cookieUser = parseUserCookie();
        if (!cookieUser || typeof cookieUser !== 'object') return;

        setCookie('user', { ...cookieUser, token: newToken });
        setInstanceTokenDefaults(newToken);
    },
    loginUrl: `${tradingTerminalBaseUrl}/auth/login`
});

export default instance;
